/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';
import { menuDefine } from '@/constants/menu';
import { objtype } from '@/constants/objtype';
import { routePathByObjId } from '@/utils/router';
import { performanceNodeTabs } from '@/constants/performance';
import { OPER_STATE_RSC_LIST, PERF_RSC_LIST } from '@/constants/topologymap-major-info';
import { getIntervalDatetimeParams } from '@/utils/date';
import { getSubTypeByObjInfo, getObjTypeIconClass as getObjTypeIconClassForTree } from '@/utils/tree';
import { isTrue } from '@/utils/boolean';
import MapAPI from '@/api/topologymap/common';
import EventPredictSettingsAPI from '@/api/settings/common/event-predict-settings';

/**
 * 트랜잭션 서비스의 E2E Agent들을 조회한다.
 * @author Ju
 * @param  {Number} objId
 * @return {Object}
 */
export function getE2eAgentList(objId) {
  return MapAPI.getAgentList(objId);
}

/**
 * 관리 대상의 주요 구성 / 성능 / 이벤트 정보를 조회한다.
 * @author Ju
 * @param  {Object}  obj           관리대상 정보 객체
 * @param  {Object}  agentInfo     E2E 에이전트 정보. E2E인 경우에만 전달
 * @param  {Boolean} isMap        토폴로지맵 여부
 * @return {Object}                Promise 객체. 결과 데이터 → ( { majorInfoData: 장비 구성/성능/이벤트 정보, predictEventHist: 사전 예측 이벤트, rscNameList: 성능 정보 이름 배열 })
 */
export function loadObjMajorInfo(obj, agentInfo, isMap = false) {
  return new Promise((resolve, reject) => {
    const objId = obj.objId;
    const objDefineId = obj.objDefineId;
    const params = {
      type: 'obj',
      objId: obj.objId,
      objDefineId,
      objtypeId: obj.objtypeId,
      managetypeId: isNil(obj.managetypeId) ? obj.objManagetypeId : obj.managetypeId,
      startDatetime: getIntervalDatetimeParams(new Date().getTime(), 'hour', 24),
      isMap,
    };

    let rscNameList = [];
    if (objDefineId !== 'SERVICE') {
      // 운영상태 && 가동률 조회 자원정보
      params.operRsc = objDefineId === 'FMS_SENSOR' || objDefineId === 'VMS_OBJECT' || objDefineId === 'WIRELESS_DEVICE' ? OPER_STATE_RSC_LIST[params.objDefineId][params.objtypeId] : OPER_STATE_RSC_LIST[params.objDefineId];
      // 성능정보 조회 자원정보
      if (objDefineId === 'FMS_SENSOR' || objDefineId === 'VMS_OBJECT' || objDefineId === 'WIRELESS_DEVICE') {
        params.chartRsc = PERF_RSC_LIST[params.objDefineId][params.objtypeId].RSC;
        rscNameList = PERF_RSC_LIST[params.objDefineId][params.objtypeId].NAME;
      } else {
        params.chartRsc = PERF_RSC_LIST[params.objDefineId].RSC;
        rscNameList = PERF_RSC_LIST[params.objDefineId].NAME;
      }
    }

    // 장비 종류 별 특화 조건
    if (objDefineId === 'E2E_SERVICE') {
      params.objId = agentInfo.objId;
      params.rscKey = agentInfo.rscKey;
      params.transactionId = agentInfo.transactionId;
      params.transactionServiceId = agentInfo.transactionServiceId;
      params.serviceId = obj.objId;
    } else if (objDefineId === 'SERVICE') {
      params.serviceId = obj.objId;
    }

    axios
      .all([
        MapAPI.getObjectMajorInfo(params),
        EventPredictSettingsAPI.getPredictEventHist({
          objId,
        }),
      ])
      .then(
        axios.spread((majorInfResponse, predictEvenResponse) => {
          const result = {
            rscNameList,
          };
          if (!isEmpty(majorInfResponse.data) && majorInfResponse.data.success) {
            result.majorInfoData = majorInfResponse.data.data;
          }
          if (!isEmpty(predictEvenResponse.data) && predictEvenResponse.data.success) {
            result.predictEventHist = predictEvenResponse.data.data;
          }
          resolve(result);
        }))
      .catch((e) => {
        reject(e);
      });
  });
}

/**
 * 관리 대상 타입에 맞는 아이콘 클래스를 반환한다.
 * @author Ju
 * @param  {String} managetypeId
 * @param  {String} objDefineId
 * @param  {String} objtypeId
 * @return {String}
 */
export function getObjTypeIconClass(managetypeId, objDefineId, objtypeId) {
  const subType = getSubTypeByObjInfo(managetypeId, objDefineId, objtypeId);
  return getObjTypeIconClassForTree({ subType, treeNodeSubTypeId: objtypeId });
}

/**
 * 운영 상태 제공 여부
 * @author Ju
 * @param  {String}  objDefineId
 * @return {Boolean}             [description]
 */
export function isVisibleOperStateOfObj(objDefineId) {
  return !(objDefineId === 'DBMS' || objDefineId === 'NETINTERFACE' || objDefineId === 'SERVICE');
}

/**
 * 성능 정보 제공 여부
 * @author Ju
 * @param  {String}  objDefineId
 * @return {Boolean}             [description]
 */
export function isVisiblePerfInfo(objDefineId) {
  return objDefineId !== 'SERVICE';
}

/**
 * E2E 에이전트 콤보박스 제공 여부
 * @author Ju
 * @param  {String}  objDefineId
 * @return {Boolean}             [description]
 */
export function isVisibleAgentCombobox(objDefineId) {
  return objDefineId === 'E2E_SERVICE';
}

/**
 * 장비의 상세 정보 화면으로 이동한다.
 * @author Ju
 * @param  {Object} obj      장비 객체
 * @param  {String} infoType 이동할 상세 화면 정보
 */
export function routeForDetailInfo(vm, obj, infoType, newBrowserTab = true) {
  const managetypeId = obj.managetypeId || obj.objManagetypeId;
  const objId = managetypeId === 'E2E' && !isNil(obj.rscKey) ? obj.rscKey.split('.')[1] : obj.objId;
  if (infoType === 'event-status') {
    routePathByObjId(vm, objId, menuDefine.EVENT, 'event-status', null, null, newBrowserTab);
  } else if (infoType === 'event-predict') {
    routePathByObjId(vm, objId, menuDefine.EVENT, 'event-predict', null, null, newBrowserTab);
  } else {
    routePathByObjId(vm, objId, menuDefine.PERF_OPER, performanceNodeTabs.summary.name, null, null, newBrowserTab);
  }
}

/**
 * 관리대상 상태 값을 boolean 형태로 반환한다.
 * 예외적으로 자원 미수집 시 down이 아닌 미수집 상태임을 표현해줘야 하는 관리 대상은 null을 반환한다.
 * @author Ju
 * @param  {Number, String, Boolean} value       상태 값
 * @param  {String} objDefineId
 * @param  {String} objtypeId
 * @return {Boolean}
 */
export function getObjStatusToBoolean(value, objDefineId, objtypeId) {
  return isNil(value) && objDefineId === 'WIRELESS_DEVICE' && objtypeId === objtype.WIRELESS_AP ? null : isTrue(value);
}



// WEBPACK FOOTER //
// ./src/utils/obj-major-info.js